import React, { useState } from "react";
import {
  CheckCircleFilled,
  ClockCircleOutlined,
  EnvironmentOutlined,
  HeartFilled,
  HeartOutlined,
  StarFilled,
} from "@ant-design/icons";
import { FaPlane } from "react-icons/fa";
import IncludedBaggageCompact from "../../Components/Common/IncludedBaggageCompact/IncludedBaggageCompact";
import { HOTELSRIVER_OFFICIAL_LOGO_URL } from "../../utils/hotelsRiverBrandLogo";
import {
  formatFx,
  formatIls,
  getAgentNetOriginal,
  getAgentNetTotalOriginal,
  getPackageCurrency,
} from "./demoFixtures";

/** Phase 0 local demo card — HRM class names; does not modify packagesResultsMockup. */

function HotelMedia({ imageUrl, hotelName }) {
  const [failed, setFailed] = useState(false);
  const src = String(imageUrl || "").trim();
  const showPhoto = Boolean(src) && !failed;

  if (!showPhoto) {
    return (
      <div className="hrm-card__media-placeholder" role="img" aria-label={hotelName || "HotelsRiver"}>
        <img src={HOTELSRIVER_OFFICIAL_LOGO_URL} alt="HotelsRiver" />
        <span>HotelsRiver</span>
      </div>
    );
  }

  return (
    <img
      src={src}
      alt={hotelName || ""}
      loading="lazy"
      decoding="async"
      onError={() => setFailed(true)}
    />
  );
}

function StarRating({ stars }) {
  const count = Math.max(0, Math.min(5, Number(stars) || 0));
  if (!count) return null;
  return (
    <span className="hrm-stars" aria-label={`${count} כוכבים`}>
      {Array.from({ length: count }).map((_, i) => (
        <StarFilled key={i} />
      ))}
    </span>
  );
}

function seatsTone(seatsLeft) {
  const n = Number(seatsLeft);
  if (!Number.isFinite(n)) return "unknown";
  if (n <= 3) return "critical";
  if (n <= 6) return "low";
  return "ok";
}

function splitClockAndDayOffset(raw) {
  const text = String(raw || "").trim();
  const match = text.match(/^(\d{1,2}:\d{2})\s*(\+\d+)?$/);
  if (!match) return { clock: text, dayOffset: null };
  return { clock: match[1], dayOffset: match[2] || null };
}

function FlightTime({ value }) {
  const { clock, dayOffset } = splitClockAndDayOffset(value);
  return (
    <strong className="hrm-flight-leg__clock">
      <span>{clock}</span>
      {dayOffset ? <sup className="hrm-flight-leg__day">{dayOffset}</sup> : null}
    </strong>
  );
}

function resolveAirlineCode(leg) {
  if (leg?.airlineCode) return String(leg.airlineCode).trim().toUpperCase();
  const fromNo = String(leg?.flightNo || "")
    .trim()
    .split(/\s+/)[0];
  if (fromNo && /^[A-Z0-9]{2}$/i.test(fromNo)) return fromNo.toUpperCase();
  return null;
}

const kiwiAirlineLogoUrl = (code, px = 128) =>
  `https://images.kiwi.com/airlines/${px}/${encodeURIComponent(code)}.png`;

function MockAirlineLogo({ leg }) {
  const code = resolveAirlineCode(leg);
  const name = String(leg?.airline || "").trim() || code || "";
  const [failed, setFailed] = useState(false);
  if (!code || failed) {
    return (
      <span
        className="hrm-airline-logo hrm-airline-logo--fallback"
        title={name || undefined}
        aria-label={name || "חברת תעופה"}
      >
        <FaPlane />
      </span>
    );
  }
  return (
    <span className="hrm-airline-logo" title={name || code} aria-label={name || code}>
      <img
        src={kiwiAirlineLogoUrl(code, 128)}
        alt={name || code}
        width={96}
        height={28}
        loading="lazy"
        decoding="async"
        onError={() => setFailed(true)}
      />
    </span>
  );
}

function AllocationBadge({ immediate, variant = "chip" }) {
  const label = immediate ? "אישור מיידי" : "ממתין לאישור";
  const Icon = immediate ? CheckCircleFilled : ClockCircleOutlined;
  return (
    <span
      className={`hrm-alloc hrm-alloc--${immediate ? "immediate" : "pending"} hrm-alloc--${variant}`}
    >
      <Icon aria-hidden />
      {label}
    </span>
  );
}

function FlightLeg({ leg, directionLabel, isDirect }) {
  if (!leg) return null;
  const stopsLabel = leg.stopsLabel || (isDirect ? "ישיר" : null);
  const seats = Number(leg.seatsLeft);
  const hasSeats = Number.isFinite(seats);
  const tone = seatsTone(seats);
  const flightCodeLabel = leg.flightNo || "";
  return (
    <div className="hrm-flight-leg">
      <div className="hrm-flight-leg__head">
        <MockAirlineLogo leg={leg} />
        <div className="hrm-flight-leg__head-body">
          <div className="hrm-flight-leg__label">
            <div className="hrm-flight-leg__label-main">
              <span className="hrm-flight-leg__dir">{directionLabel}</span>
              {flightCodeLabel ? (
                <span className="hrm-flight-leg__flight-no" title={leg.airline || undefined}>
                  {flightCodeLabel}
                </span>
              ) : null}
            </div>
            {stopsLabel ? <span className="hrm-flight-leg__stops">{stopsLabel}</span> : null}
          </div>
          <div className="hrm-flight-leg__times">
            <FlightTime value={leg.depart} />
            <span className="hrm-flight-leg__sep" aria-hidden>
              <FaPlane />
            </span>
            <FlightTime value={leg.arrive} />
          </div>
          <div className="hrm-flight-leg__meta">
            <span className="hrm-flight-leg__route">
              {leg.from} → {leg.to}
            </span>
            {leg.duration ? <span className="hrm-flight-leg__dur">{leg.duration}</span> : null}
          </div>
          {hasSeats ? (
            <div
              className={`hrm-flight-leg__seats hrm-flight-leg__seats--${tone}`}
              title="מקומות פנויים על הטיסה"
            >
              {seats} מקומות פנויים
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export default function DemoPackageCard({
  pkg,
  viewMode = "list",
  compared = false,
  favorited = false,
  onToggleCompare,
  onToggleFavorite,
  onView,
  onMoreFlights,
}) {
  const baggage = pkg.baggage || {};
  const hasBaggage = !baggage.none && Array.isArray(baggage.bullets) && baggage.bullets.length > 0;
  const moreFlights = Math.max(0, Number(pkg.flightOptionsCount || 1) - 1);
  const currency = getPackageCurrency(pkg);
  const pax = Math.max(1, Number(pkg.paxCount) || 2);
  const netPerPersonFx = getAgentNetOriginal(pkg);
  const netTotalFx = getAgentNetTotalOriginal(pkg, pax);
  // Do not fall back to pricePerPerson as ILS (that field is FX for Hubwayz).
  const netPerPersonIls = Number(pkg.agentNetPerPersonIls);
  const showIls = Number.isFinite(netPerPersonIls) && netPerPersonIls > 0;
  const netTotalIls = showIls ? netPerPersonIls * pax : null;

  return (
    <article
      className={`hrm-card${viewMode === "grid" ? " hrm-card--grid" : ""}`}
      data-package-id={pkg.id}
    >
      <div className="hrm-card__media">
        <HotelMedia imageUrl={pkg.imageUrl} hotelName={pkg.hotelName} />
        <div className="hrm-card__media-badges">
          <AllocationBadge immediate={!!pkg.immediateConfirm} variant="overlay" />
        </div>
      </div>

      <div className="hrm-card__body">
        <div className="hrm-card__title-row">
          <h3 className="hrm-card__hotel">{pkg.hotelName}</h3>
          <StarRating stars={pkg.stars} />
        </div>
        <p className="hrm-card__location">
          <EnvironmentOutlined aria-hidden />
          {pkg.city}, {pkg.country}
        </p>
        <div className="hrm-card__tags">
          <span className="hrm-tag hrm-tag--flight">טיסה + מלון</span>
          <span className="hrm-tag">{pkg.boardLabel}</span>
          <span className="hrm-tag">{pkg.roomType}</span>
          {pkg.directFlight ? <span className="hrm-tag">טיסה ישירה</span> : null}
          {pkg.transferIncluded ? <span className="hrm-tag">כולל העברה</span> : null}
        </div>
        <p className="hrm-card__dates">
          {pkg.dateRangeLabel} • {pkg.nights} לילות
        </p>

        <div className="hrm-card__baggage">
          {hasBaggage ? (
            <IncludedBaggageCompact bullets={baggage.bullets} variant="metaSubrow" />
          ) : (
            <span className="hrm-baggage-none">ללא כבודה כלולה</span>
          )}
        </div>

        {pkg.outbound || pkg.inbound ? (
          <div className="hrm-card__flights">
            <div className="hrm-card__flights-head">
              <FaPlane aria-hidden />
              <span>הטיסה הזולה ביותר בהצעה</span>
            </div>
            <div className="hrm-card__flights-legs">
              <FlightLeg leg={pkg.outbound} directionLabel="הלוך" isDirect={pkg.directFlight} />
              <FlightLeg leg={pkg.inbound} directionLabel="חזור" isDirect={pkg.directFlight} />
            </div>
            {moreFlights > 0 ? (
              <button
                type="button"
                className="hrm-flight-options"
                onClick={() => onMoreFlights?.(pkg)}
              >
                +{moreFlights} אפשרויות טיסה נוספות
              </button>
            ) : null}
          </div>
        ) : null}
      </div>

      <div className="hrm-card__action">
        <div className="hrm-card__price-block">
          <div className="hrm-card__price">
            <span className="hrm-card__price-fx">{formatFx(netPerPersonFx, currency)}</span>
            {showIls ? (
              <span className="hrm-card__price-ils">{formatIls(netPerPersonIls)}</span>
            ) : null}
          </div>
          <div className="hrm-card__price-meta">לאדם · נטו</div>
          <div className="hrm-card__total">
            סה״כ ל־{pax}{" "}
            <span className="hrm-card__total-fx">{formatFx(netTotalFx, currency)}</span>
            {showIls && netTotalIls != null ? (
              <>
                <span className="hrm-card__total-sep" aria-hidden>
                  ·
                </span>
                <span className="hrm-card__total-ils">{formatIls(netTotalIls)}</span>
              </>
            ) : null}
          </div>
        </div>
        <button type="button" className="hrm-btn-primary" onClick={() => onView?.(pkg)}>
          המשך להזמנה
        </button>
        <div className="hrm-card__tools">
          <label className="hrm-compare">
            <input
              type="checkbox"
              checked={compared}
              onChange={() => onToggleCompare?.(pkg.id)}
            />
            השוואה
          </label>
          <button
            type="button"
            className={`hrm-fav${favorited ? " hrm-fav--on" : ""}`}
            aria-label={favorited ? "הסר ממועדפים" : "הוסף למועדפים"}
            onClick={() => onToggleFavorite?.(pkg.id)}
          >
            {favorited ? <HeartFilled /> : <HeartOutlined />}
          </button>
        </div>
      </div>
    </article>
  );
}
